import { Keyboard, Search } from "lucide-react";
import { Dialog } from "radix-ui";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import {
  isTypingTarget,
  matchShortcut,
  SEQUENCE_MS,
  type ShortcutAction,
  SHORTCUTS,
} from "@/app/shortcuts";
import { useAuth } from "@/auth/use-auth";
import { cn } from "@/lib/utils";

import { CommandPalette } from "./CommandPalette";
import { ScannerDetector } from "./scannerDetector";

interface SearchContextValue {
  /** Open the search box (optionally with text; `scan` opens a single hit at once). */
  openSearch(initial?: string, options?: { scan?: boolean }): void;
  showShortcuts(): void;
  /**
   * Turn the global shortcuts + scanner lookup off while a screen owns the keyboard (the stock
   * count scan mode, T11-20). Returns the function that turns them on again.
   */
  suspendShortcuts(): () => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error("useSearch must be used inside <SearchProvider>");
  }
  return ctx;
}

/**
 * A screen that owns the keyboard (scan mode) switches the global ⌘K / `/` / `n` / `g o` and the
 * scanner lookup off while it is mounted (T11-20). Outside the admin shell it does nothing.
 */
export function useSuspendShortcuts(active = true): void {
  const ctx = useContext(SearchContext);
  const suspend = ctx?.suspendShortcuts;
  useEffect(() => {
    if (!active || !suspend) {
      return;
    }
    return suspend();
  }, [active, suspend]);
}

/** Single-key shortcuts wait this long: a scanner burst that starts with `n` / `g` cancels them. */
const SINGLE_KEY_DELAY_MS = 40;

function ShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation("search");
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-[71] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-5 shadow-lg"
        >
          <Dialog.Title className="flex items-center gap-2 text-lg font-bold">
            <Keyboard className="size-5" aria-hidden />
            {t("shortcuts.title")}
          </Dialog.Title>
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {SHORTCUTS.map((s) => (
              <div key={`${s.action}-${s.keys.join("")}`} className="contents">
                <dt className="flex gap-1">
                  {s.keys.map((k, i) => (
                    <kbd
                      key={`${k}-${String(i)}`}
                      className="rounded border bg-muted px-1.5 font-mono text-xs"
                    >
                      {k}
                    </kbd>
                  ))}
                </dt>
                <dd>{t(`shortcuts.${s.action}`)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-xs text-muted-foreground">{t("shortcuts.note")}</p>
          <Dialog.Close className="mt-4 min-h-touch rounded-md border px-4 text-sm font-medium hover:bg-accent">
            {t("close")}
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Global search + keyboard shortcuts + barcode scanner (T11-07). Lives inside the admin shell;
 * one `keydown` listener on `window` for ⌘K, `/`, `n`, `g o`, `g p`, `?` and HID scans.
 */
export function SearchProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState("");
  const [scan, setScan] = useState(false);
  const [opening, setOpening] = useState(0);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const pathRef = useRef(location.pathname);
  const openRef = useRef(open || shortcutsOpen);
  const suspendedRef = useRef(0);
  useEffect(() => {
    pathRef.current = location.pathname;
    openRef.current = open || shortcutsOpen;
  });

  const openSearch = useCallback((text = "", options: { scan?: boolean } = {}) => {
    setInitial(text);
    setScan(Boolean(options.scan));
    setOpening((n) => n + 1);
    setOpen(true);
  }, []);
  const showShortcuts = useCallback(() => {
    setShortcutsOpen(true);
  }, []);
  const suspendShortcuts = useCallback(() => {
    suspendedRef.current += 1;
    let done = false;
    return () => {
      if (!done) {
        done = true;
        suspendedRef.current -= 1;
      }
    };
  }, []);

  const run = useCallback(
    (action: ShortcutAction) => {
      switch (action) {
        case "open_search":
          openSearch();
          break;
        case "show_shortcuts":
          setShortcutsOpen(true);
          break;
        case "new_product":
          if (can("products.write")) {
            void navigate("/products/new");
          }
          break;
        case "go_orders":
          void navigate("/orders");
          break;
        case "go_products":
          void navigate("/products");
          break;
      }
    },
    [can, navigate, openSearch],
  );

  useEffect(() => {
    const scanner = new ScannerDetector();
    let composing = false;
    let prefix: { key: string; at: number } | null = null;
    let deferred: ReturnType<typeof setTimeout> | null = null;
    const cancelDeferred = () => {
      if (deferred) {
        clearTimeout(deferred);
        deferred = null;
      }
    };

    const onCompositionStart = () => {
      composing = true;
    };
    const onCompositionEnd = () => {
      composing = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (suspendedRef.current > 0) {
        return;
      }
      const typing = isTypingTarget(event.target);
      const imeBusy = event.isComposing || event.keyCode === 229 || composing;

      // HID scanners: a fast burst + Enter outside any field = a lookup
      if (
        !typing &&
        !imeBusy &&
        !openRef.current &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        if (event.key.length === 1 && deferred) {
          // a second key right after a single-key shortcut: that was a scan, not a shortcut
          cancelDeferred();
        }
        const code = scanner.feed({ key: event.key, time: event.timeStamp || performance.now() });
        if (code) {
          event.preventDefault();
          cancelDeferred();
          prefix = null;
          openSearch(code, { scan: true });
          return;
        }
      }

      const now = event.timeStamp || performance.now();
      const pendingPrefix = prefix && now - prefix.at <= SEQUENCE_MS ? prefix.key : null;
      const match = matchShortcut(event, {
        pathname: pathRef.current,
        pendingPrefix,
        composing,
      });
      if (match.prefix) {
        prefix = { key: match.prefix, at: now };
        return;
      }
      prefix = null;
      if (!match.action) {
        return;
      }
      if (openRef.current && match.action !== "open_search") {
        return;
      }
      event.preventDefault();
      if (event.metaKey || event.ctrlKey) {
        run(match.action);
        return;
      }
      const action = match.action;
      cancelDeferred();
      deferred = setTimeout(() => {
        deferred = null;
        if (!scanner.pending) {
          run(action);
        }
      }, SINGLE_KEY_DELAY_MS);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("compositionstart", onCompositionStart, true);
    window.addEventListener("compositionend", onCompositionEnd, true);
    return () => {
      cancelDeferred();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("compositionstart", onCompositionStart, true);
      window.removeEventListener("compositionend", onCompositionEnd, true);
    };
  }, [openSearch, run]);

  const value = useMemo(
    () => ({ openSearch, showShortcuts, suspendShortcuts }),
    [openSearch, showShortcuts, suspendShortcuts],
  );

  return (
    <SearchContext value={value}>
      {children}
      {open ? (
        <CommandPalette
          key={opening}
          open
          onOpenChange={setOpen}
          initialQuery={initial}
          autoOpenExact={scan}
        />
      ) : null}
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </SearchContext>
  );
}

/** Header button (tablets / phones have no ⌘K): opens the same search box. */
export function SearchButton({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const { openSearch } = useSearch();
  return (
    <button
      type="button"
      data-slot="search-button"
      aria-label={t("header.search_hint")}
      aria-keyshortcuts="Meta+K Control+K /"
      onClick={() => {
        openSearch();
      }}
      className={cn(
        "inline-flex min-h-touch items-center gap-2 rounded-md border text-sm text-muted-foreground hover:bg-accent",
        compact ? "size-touch justify-center border-transparent" : "min-w-56 px-3",
      )}
    >
      <Search className="size-5 shrink-0" aria-hidden />
      {compact ? null : (
        <>
          <span className="flex-1 text-left">{t("header.search")}</span>
          <kbd className="hidden rounded border bg-muted px-1.5 font-mono text-xs lg:inline">
            ⌘K
          </kbd>
        </>
      )}
    </button>
  );
}
